import type { popstore } from '@ps/types';
import { encode as encodeBlurhash } from 'blurhash';

/**
 * Subset of {@link popstore.MediaFileMetadata} the caller wants
 * extracted. Defaults to the four image-quality keys
 * ({@link kAllImageMetadataKeys}); consumers that only need
 * dimensions can pass `['width', 'height']` to skip the blurhash +
 * brightness canvas passes (each one decodes the full pixel buffer).
 */
export type ImageMetadataKey = 'width' | 'height' | 'blurhash' | 'brightness';

export const kAllImageMetadataKeys: ImageMetadataKey[] = [
  'width',
  'height',
  'blurhash',
  'brightness',
];

export interface ExtractImageMetadataOptions {
  /** Which metadata fields to extract. Defaults to {@link kAllImageMetadataKeys}. */
  keys?: ImageMetadataKey[];
  /**
   * BlurHash X-axis component count (1–9). Higher = more detail,
   * larger string. Default 4 — same as PopshopRN.
   */
  blurhashComponentsX?: number;
  /** BlurHash Y-axis component count (1–9). Default 4. */
  blurhashComponentsY?: number;
  /**
   * Pixel side length the image is downsampled to before computing
   * blurhash / brightness. Smaller is faster and not visibly worse
   * for the use cases (blur preview + avg brightness). Default 32.
   */
  sampleSize?: number;
}

/**
 * Pull client-extractable metadata from an image File / Blob /
 * object URL. Returns whatever keys could be computed — extraction
 * never throws; missing keys just stay undefined. The caller decides
 * what to do with partial results.
 *
 * Mirrors PopshopRN's per-feature metadata extraction (which scatters
 * `loadImage`, `getImageBrightness`, `encodeImageElToBlurhash` calls
 * across `usePostMediaFileService` etc) — collapsed here into one
 * function so consumers don't reimplement the canvas glue every
 * time.
 */
export async function extractImageMetadata(
  source: File | Blob | string,
  options: ExtractImageMetadataOptions = {},
): Promise<popstore.MediaFileMetadata> {
  const {
    keys = kAllImageMetadataKeys,
    blurhashComponentsX = 4,
    blurhashComponentsY = 4,
    sampleSize = 32,
  } = options;

  const objectUrl =
    typeof source === 'string' ? source : URL.createObjectURL(source);
  const shouldRevokeUrl = typeof source !== 'string';

  const result: popstore.MediaFileMetadata = {};
  try {
    const image = await loadHtmlImage(objectUrl);

    if (keys.includes('width')) result.width = image.naturalWidth;
    if (keys.includes('height')) result.height = image.naturalHeight;

    const needsPixelData =
      keys.includes('blurhash') || keys.includes('brightness');
    if (!needsPixelData) return result;

    const pixelData = drawToCanvas(image, sampleSize);
    if (!pixelData) return result;

    if (keys.includes('blurhash')) {
      try {
        result.blurhash = encodeBlurhash(
          pixelData.data,
          pixelData.width,
          pixelData.height,
          blurhashComponentsX,
          blurhashComponentsY,
        );
      } catch {
        // BlurHash encoding throws when the canvas read is tainted
        // (cross-origin image without CORS headers). Swallow — the
        // upload doesn't need the placeholder.
      }
    }

    if (keys.includes('brightness')) {
      result.brightness = computeAverageBrightness(pixelData.data);
    }

    return result;
  } catch {
    return result;
  } finally {
    if (shouldRevokeUrl) URL.revokeObjectURL(objectUrl);
  }
}

function loadHtmlImage(source: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.setAttribute('crossOrigin', 'anonymous');
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.src = source;
  });
}

function drawToCanvas(
  image: HTMLImageElement,
  maxSide: number,
): ImageData | null {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  if (naturalWidth === 0 || naturalHeight === 0) return null;

  const scale = Math.min(1, maxSide / Math.max(naturalWidth, naturalHeight));
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0, width, height);

  try {
    return ctx.getImageData(0, 0, width, height);
  } catch {
    // Tainted canvas — same swallow as blurhash above.
    return null;
  }
}

function computeAverageBrightness(pixels: Uint8ClampedArray): number {
  // Walk RGBA stride. Skip alpha. Use the rec.601 luma coefficients
  // — same approximation PopshopRN's `getImageBrightness` uses, gives
  // perceived rather than mathematical brightness.
  let totalLuma = 0;
  let sampleCount = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    const red = pixels[i];
    const green = pixels[i + 1];
    const blue = pixels[i + 2];
    totalLuma += 0.299 * red + 0.587 * green + 0.114 * blue;
    sampleCount++;
  }
  return sampleCount === 0 ? 0 : totalLuma / sampleCount;
}
